import {useSelector} from 'react-redux';

import {useGetAutoDeliveryDetailsExtendedDcx} from '@/api/consumerApi';
import {AUTO_DELIVERY_FREQUENCY_TYPES} from '@/consts/icc/iccAutoDeliveryStatusTypes';
import {selectLanguageCode} from '@/state/selectors/global';
import type {AutoDeliveryFrequencyValue} from '@/types/icc/autoDeliveryDetails';
import dateTimeUtils from '@/utils/dateTimeUtils';
import {normalizeApiDate} from '@/utils/normalizeApiDate';

const FREQUENCY_DAYS_OFFSET: Record<string, number> = {
    [AUTO_DELIVERY_FREQUENCY_TYPES.DAILY]: 1,
    [AUTO_DELIVERY_FREQUENCY_TYPES.WEEKLY]: 7,
    [AUTO_DELIVERY_FREQUENCY_TYPES.BIWEEKLY]: 14,
    [AUTO_DELIVERY_FREQUENCY_TYPES.MONTHLY]: 30,
    [AUTO_DELIVERY_FREQUENCY_TYPES.BIMONTHLY]: 60,
};

const getNextDeliveryDateAfterSkip = (
    nextDeliveryDate: Date,
    frequency: AutoDeliveryFrequencyValue
): Date | undefined => {
    if (!frequency) return undefined;

    const daysOffset = FREQUENCY_DAYS_OFFSET[frequency.toUpperCase()];

    if (daysOffset === undefined) return undefined;

    const skipDate = new Date(nextDeliveryDate);

    skipDate.setDate(skipDate.getDate() + daysOffset);

    return skipDate;
};

const useDeliveryDate = () => {
    const languageCode = useSelector(selectLanguageCode);
    const {
        data: {
            autoDelivery: {asapDeliveryDate, nextDeliveryDate, isSkipDeliveryAvailable, frequency},
        },
    } = useGetAutoDeliveryDetailsExtendedDcx();

    const now = new Date();
    const asapDelivery = normalizeApiDate(asapDeliveryDate) ?? now;
    const nextDelivery = normalizeApiDate(nextDeliveryDate) ?? now;

    const skipDeliveryDate = getNextDeliveryDateAfterSkip(nextDelivery, frequency);

    return {
        startDeliveryDay: nextDelivery.getDate(),
        startDeliveryMonth: dateTimeUtils.getShortMonth(nextDelivery, languageCode),
        startDeliveryDate: dateTimeUtils.getDayAndMonth(nextDelivery, languageCode),
        days: dateTimeUtils.getDateDiffInDays(now, asapDelivery),
        asapDeliveryDay: asapDelivery.getDate(),
        asapDeliveryMonth: dateTimeUtils.getMonth(asapDelivery, languageCode),
        asapDeliveryDate: dateTimeUtils.getWeekAndDayAndMonth(asapDelivery, languageCode),
        skipDeliveryDay: skipDeliveryDate?.getDate(),
        skipDeliveryMonth: skipDeliveryDate ? dateTimeUtils.getMonth(skipDeliveryDate, languageCode) : undefined,
        skipDeliveryShortMonth: skipDeliveryDate
            ? dateTimeUtils.getShortMonth(skipDeliveryDate, languageCode)
            : undefined,
        isSkipDeliveryAvailable,
        asapDeliveryDateRaw: asapDeliveryDate,
    };
};

export default useDeliveryDate;
